<template>
  <div class="settings-block invoices">
    <div class="settings-block__title">
      <div class="justify-space-between">
        <div class="settings-block__title-text">{{ t('Invoices') }}</div>
      </div>
    </div>
    <ui-table
      :loading="dataInvoicesLoading"
      :headers="headers"
      :items="rows"
      :use-search="false"
      :virtualize="false"
      :rows-per-page-count="10"
    >
      <template #item.total="{ item }">
        {{ numeral(item.total / 100).format('0.00') }}
      </template>
      <template #item.status="{ item }">
        <span v-if="statuses[item.status]" :class="statuses[item.status].class">{{
          statuses[item.status].text
        }}</span>
      </template>
      <template #item.download="{ item }">
        <a target="_blank" class="download-link" :href="invoiceDownloadUrl(item)">
          <ui-icon size="12" :color="$colors.green" icon="fas fa-file-download" />
          {{ t('Download') }}
        </a>
      </template>
    </ui-table>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import moment from 'moment-timezone';
import config from '@/config';
import numeral from 'numeral';
import { useI18n, useColors } from '@/composables';
import { useStore } from '@/store';

type InvoiceRow = {
  id: string;
  date: string;
  total: number;
  status: string;
};

const props = withDefaults(
  defineProps<{
    dataInvoices?: unknown[] | null;
    format?: string;
  }>(),
  {
    dataInvoices: null,
    format: 'DD MMMM YYYY',
  },
);

const store = useStore();
const $colors = useColors();
const { t, locale } = useI18n();

const dataInvoicesLoading = ref(true);
const rows = ref<InvoiceRow[]>([]);

const statuses = computed((): Record<string, { text: string; class: string }> => {
  void locale.value;
  return {
    paid: {
      text: t('Paid'),
      class: 'invoices__status--paid',
    },
    payment_due: {
      text: t('Payment due'),
      class: 'invoices__status--payment-due',
    },
    not_paid: {
      text: t('Not paid'),
      class: 'invoices__status--not-paid',
    },
    voided: {
      text: t('Voided'),
      class: 'invoices__status--neutral',
    },
    pending: {
      text: t('Pending'),
      class: 'invoices__status--neutral',
    },
  };
});

const headers = computed(() => {
  void locale.value;
  return [
    {
      title: t('Date'),
      align: 'left',
      sortable: false,
      value: 'date',
    },
    {
      title: t('Sum'),
      align: 'center',
      sortable: false,
      value: 'total',
    },
    {
      title: t('Status'),
      align: 'center',
      sortable: false,
      value: 'status',
    },
    {
      title: t('Download'),
      align: 'center',
      sortable: false,
      value: 'download',
    },
  ];
});

const invoiceDownloadUrl = (item: { id: string }) => {
  const token = store.authToken ?? '';
  return `${config.api.baseURL}/billing/invoices/${item.id}?accesstoken=${token}`;
};

watch(
  () => props.dataInvoices,
  data => {
    if (data == null) {
      return;
    }
    if (!Array.isArray(data)) {
      rows.value = [];
      dataInvoicesLoading.value = false;
      return;
    }
    rows.value = data.map(entry => {
      const item = entry as { id: string; date: number; total: number; status: string };
      return {
        ...item,
        date: moment.unix(item.date).format(props.format),
      };
    });
    dataInvoicesLoading.value = false;
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.invoices {
  &__status {
    &--paid {
      color: var(--color-green);
    }

    &--payment-due {
      color: var(--color-tangerine);
    }

    &--not-paid {
      color: var(--color-red);
    }

    &--neutral {
      color: var(--color-gray40);
    }
  }

  :deep(.table-body .table-row .table-cell) {
    width: 20%;
    border-right: none !important;
    text-align: center;
    box-sizing: border-box;

    &:first-child {
      width: 40%;
      text-align: left;
    }

    a {
      text-decoration: none;
    }
  }
}

.download-link {
  text-decoration: none;
  color: var(--color-green);
  font-size: 13px;
}
</style>
